import {
  BrandclubStoreConfigUtils,
  isInstagramOrFBBrowser,
  PATHNAME_CHECKER,
  setupDeepView,
  TrackingParams,
  useFingerprintJSContext
} from "@brandclub/common-ui";
import { SessionData } from "branch-sdk";
import _get from "lodash/get";
import _isEqual from 'lodash/isEqual';
import _omitBy from "lodash/omitBy";
import queryString from "query-string";
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Params, useLocation } from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import useBranchIOSession from "../../utils/hooks/useBranchIOSession";

export const LocationTrackingContext = createContext<
  [TrackingParams, (p: Params) => void, boolean]
>([null as unknown as TrackingParams, () => { }, false] as never);

export const LocationTrackingContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const [params, setParams] = useState<Params>({});
  const [hasApp, setHasApp] = useState<boolean>(false);
  const domainConfig = useAppSelector(({ appConfig }) => appConfig?.domainConfig);
  const mainEntity = useAppSelector(({ mainEntity }) => mainEntity);
  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);
  const branchIOSessionData: SessionData | null = useBranchIOSession();

  const visitorTracking = useFingerprintJSContext();
  const [trackingParams, setTrackingParams] = useState<TrackingParams | null>(null);

  // Memoize the function to parse query parameters to prevent unnecessary calculations
  const parseQueryParams = useCallback((search: string) => {
    try {
      const parsed = queryString.parse(search) || {};
      return _omitBy(parsed, (_, k) => {
        const key = k?.toLowerCase();
        return key?.startsWith("$og_");
      });
    } catch (error) {
      // Handle parsing errors gracefully
      console.error("Error parsing query parameters:", error);
      return {};
    }
  }, []);

  // Memoize tracking parameters calculation
  const newTrackingParams: TrackingParams | null = useMemo(() => {
    // Return early if essential data is not available yet
    if (!visitorTracking?.visitorSessionId || brandEntity?.loading || mainEntity?.loading) {
      return null;
    }

    try {
      const appId = BrandclubStoreConfigUtils.getWebAppId();
      const {
        sku,
        referralCode,
        jl_id,
        affiliateLinkId,
        jl_s,
        jl_m, // traffic type = organic / email / partner  / referral
        jl_c, // purpose
        brandclubId: brandclubIdFromSearchParams,
        brandId: brandIdFromSearchParams,
        brandName: brandNameFromSearchParams,
        invitedBy,
        utm_source,
        utm_medium,
        utm_campaign,
        _branch_referrer, // skip this one from passing
        _branch_match_id,
        ...queryParamsAll
      } = parseQueryParams(location?.search);

      // Extract SKU from path
      let skuFromPath: string | undefined = undefined;
      const pdpMatch = PATHNAME_CHECKER.isPDP(location.pathname);
      if (pdpMatch && pdpMatch[1]) {
        skuFromPath = pdpMatch[1];
      }

      const skuFromParams = _get(params, ["sku"]);
      const brandclubIdFromParams = _get(params, ["brandclubId"]);
      const brandIdFromParams = _get(params, ["brandId"]);
      const brandNameFromParams = _get(params, ["brandName"]);

      const brandclubIdFromBrandEntity = brandEntity?.entity?.brandclubId;
      const brandIdFromBrandEntity = brandEntity?.entity?.brandId;
      const brandNameFromBrandEntity = brandEntity?.entity?.name;
      let brandId = brandIdFromBrandEntity || brandIdFromSearchParams || brandIdFromParams || trackingParams?.brandId;
      if (mainEntity?.entity?.brandId) {
        brandId = mainEntity?.entity?.brandId;
      }
      const brandPixels = mainEntity?.entity?.pixels ? ({ brandId, pixels: mainEntity?.entity?.pixels, }) : trackingParams?.brandPixels;
      return {
        ...queryParamsAll,
        appId: appId as string | undefined,
        vs_id: visitorTracking?.visitorSessionId,
        v_id: visitorTracking?.uniqueVisitorId,

        sku: (skuFromPath || sku || skuFromParams) as string | undefined,

        brandclubId: (brandclubIdFromBrandEntity || brandclubIdFromSearchParams || brandclubIdFromParams || trackingParams?.brandclubId) as string | undefined,
        brandId: brandId as string | undefined,
        brandName: (brandNameFromBrandEntity || brandNameFromSearchParams || brandNameFromParams || trackingParams?.brandName) as string | undefined,
        brandPixels,
        invitedBy: invitedBy as string | undefined,
        referralCode: referralCode as string | undefined,

        jl_id: jl_id as string | undefined,
        affiliateLinkId: affiliateLinkId as string | undefined,

        jl_s: jl_s as string | undefined,
        jl_m: jl_m as string | undefined,
        jl_c: jl_c as string | undefined,

        utm_source: utm_source as string | undefined,
        utm_medium: utm_medium as string | undefined,
        utm_campaign: utm_campaign as string | undefined,

        storePlatformType: "web",
        storeDomainName: domainConfig?.domainName,
        storeBrandId: domainConfig?.brandId,
        storeBrandingType: domainConfig?.storeBrandingType,
      };
    } catch (error) {
      console.error("Error calculating tracking parameters:", error);
      return trackingParams; // Return existing params on error to avoid breaking app
    }
  }, [
    location?.search,
    location?.pathname,
    visitorTracking?.visitorSessionId,
    visitorTracking?.uniqueVisitorId,
    params,
    domainConfig?.domainName,
    domainConfig?.brandId,
    domainConfig?.storeBrandingType,
    mainEntity?.entity?.pixels,
    mainEntity?.entity?.brandId,
    mainEntity?.loading,
    brandEntity?.entity?.brandclubId,
    brandEntity?.entity?.brandId,
    brandEntity?.entity?.name,
    brandEntity?.loading,
    parseQueryParams,
    trackingParams,
  ]);

  // Update tracking params and dispatch event only when they change
  useEffect(() => {
    if (!newTrackingParams) {
      return;
    }

    if (!_isEqual(newTrackingParams, trackingParams)) {
      try {
        window.dispatchEvent(
          new CustomEvent("bc-init", {
            bubbles: true,
            detail: newTrackingParams,
          })
        );
        setTrackingParams(newTrackingParams);
      } catch (error) {
        console.error("Error dispatching tracking event:", error);
      }
    }
  }, [newTrackingParams, trackingParams]);

  // Setup Branch IO when data is available
  useEffect(() => {
    const setupBranch = async () => {
      if (!branchIOSessionData || !trackingParams) {
        return;
      }

      try {
        const data = branchIOSessionData;
        if (data?.has_app) {
          setHasApp(true);
        }

        await setupDeepView({
          ...data?.data_parsed,
          ...trackingParams,
          $uri_redirect_mode: isInstagramOrFBBrowser() ? 2 : 0,
          $after_click_url: window.location.href,
        });
      } catch (error) {
        console.error("Error setting up Branch.io:", error);
      }
    };

    setupBranch();
  }, [branchIOSessionData, trackingParams]);

  // Memoize the context value to prevent unnecessary renders in consuming components
  const contextValue = useMemo<[TrackingParams, (p: Params) => void, boolean]>(
    () => [trackingParams as TrackingParams, setParams, hasApp],
    [trackingParams, hasApp]
  );

  // Only render children when tracking params are available
  if (!trackingParams || !contextValue) {
    return null; // Consider rendering a loading indicator if appropriate
  }

  return (
    <LocationTrackingContext.Provider value={contextValue}>
      {children}
    </LocationTrackingContext.Provider>
  );
};

export const useLocationTrackingParams = () => {
  return React.useContext(LocationTrackingContext);
};
